import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";
import { buildRestaurantDataArchive, chooseRestaurantArchiveDestination } from "../local/restaurantDataArchive";

const rpc = async (name, params, externalSignal) => {
  if (!supabase) throw new Error("Inicia sesion para gestionar el servicio.");
  let release = () => {};
  let result;
  try { result = await withRequestDeadline(
    signal => {
      if (!externalSignal) return supabase.rpc(name, params).abortSignal(signal);
      const controller = new AbortController();
      const abort = () => controller.abort();
      externalSignal.addEventListener("abort", abort, { once: true });
      signal.addEventListener("abort", abort, { once: true });
      release = () => { externalSignal.removeEventListener("abort", abort); signal.removeEventListener("abort", abort); };
      if (externalSignal.aborted || signal.aborted) abort();
      return supabase.rpc(name, params).abortSignal(controller.signal);
    },
    { message: "La respuesta tardo demasiado. Revisa el estado antes de repetir la solicitud." }
  ); } finally { release(); }
  if (externalSignal?.aborted) throw new DOMException("Descarga cancelada.", "AbortError");
  const { data, error } = result;
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") {
      throw new Error("Esta gestion todavia no esta habilitada en este entorno. Contacta a mozzoappenterprise@gmail.com.");
    }
    throw new Error(error.message || "No se pudo completar la gestion.");
  }
  return data;
};

export const getRestaurantLifecycle = (companyId) =>
  rpc("get_restaurant_service_status", { p_company_id: companyId });

export const cancelRestaurantService = (companyId, requestId, retainPersonalData, confirmed) =>
  rpc("cancel_restaurant_service", {
    p_company_id: companyId,
    p_request_id: requestId,
    p_retain_personal_data: retainPersonalData === true,
    p_confirmed: confirmed === true,
  });

export const reactivateRestaurantService = (companyId) =>
  rpc("reactivate_restaurant_service", { p_company_id: companyId });

export const revokeRestaurantRetention = (companyId) =>
  rpc("revoke_restaurant_data_retention", { p_company_id: companyId });

export const exportRestaurantDataset = async (companyId, dataset, onProgress = () => {}) => {
  const manifest = await rpc("export_restaurant_data", {
    p_company_id: companyId, p_dataset: "manifest",
  });
  if (!manifest.datasets.includes(dataset)) throw new Error("Datos no disponibles para descargar.");
  const rows = [];
  let cursor = null;
  const seen = new Set();
  do {
    const page = await rpc("export_restaurant_data", {
      p_company_id: companyId, p_dataset: dataset, p_after: cursor, p_limit: 200,
    });
    rows.push(...page.rows);
    onProgress(rows.length);
    cursor = page.nextCursor;
    if (cursor && seen.has(cursor)) throw new Error("La descarga no avanzo. Reintenta para evitar un archivo incompleto.");
    if (cursor) seen.add(cursor);
    // A failed/oversized export must not masquerade as a complete download.
    if (rows.length > 100000) throw new Error("El archivo requiere una exportacion asistida. Contacta a soporte; no se descargaron datos incompletos.");
  } while (cursor);
  return { ...manifest, dataset, rows };
};

export const getRestaurantExportManifest = (companyId) =>
  rpc("export_restaurant_data", { p_company_id: companyId, p_dataset: "manifest" });

export const downloadRestaurantArchive = async (companyId, onProgress, signal) => {
  const destination = await chooseRestaurantArchiveDestination();
  return buildRestaurantDataArchive({
    companyId, destination, signal, onProgress,
    fetchPage: (dataset, cursor, abortSignal) => rpc("export_restaurant_archive", {
      p_company_id: companyId, p_dataset: dataset, p_after: cursor, p_limit: 50,
    }, abortSignal),
  });
};

export const listRestaurantDataTasks = () => rpc("list_restaurant_data_tasks");
export const completeRestaurantDataTask = (taskId, evidenceReference) =>
  rpc("complete_restaurant_data_task", { p_task_id: taskId, p_evidence_reference: evidenceReference });

export const downloadRestaurantData = (data) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `mozzo-${data.dataset}-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
